import { Alert } from '@mui/material';
import { DateTime } from 'luxon';
import {
  BenefitActivationDTO,
  BenefitActivationDTOBenefitEnum,
  BenefitTemplateItemDTO,
  BulkActivationResultItemDTO,
} from 'probonio-shared-ui/api';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNewDialogState } from '../../../../../component/dialog';
import {
  ActivationDialog,
  DeleteActivationDialog,
  SingleEditActivationDialog,
  useActivationDialogState,
  useEmployeeActivationSaveFunctions,
} from '../../../../benefits';
import { SingleActivationResultDialog } from '../../../../benefits/ActivationDialog/SingleActivationResultDialog';
import { useBoundActivationComponent } from '../../../../benefits/ActivationDialog/useBoundActivationComponent';
import { BenefitProvider } from '../../../../benefits/BenefitContext';
import { BenefitRef } from '../../../../benefits/BenefitsCell';
import { useDeleteBenefitActivationMutation } from '../hooks/useDeleteBenefitActivationMutation';
import { useProfileEmployee } from '../hooks/useProfileEmployee';
import ProfileBenefitActionsContext from './useBenefitActivationModal';

export const BenefitActivationModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('benefitsModule');
  const { employee } = useProfileEmployee();
  const activationDialog = useActivationDialogState();
  const deleteDialog = useNewDialogState();
  const stopDialog = useNewDialogState();
  const { deleteActivation } = useDeleteBenefitActivationMutation();
  const [activationToEdit, setActivationToEdit] = useState<BenefitActivationDTO>();
  const [benefitRefToEdit, setBenefitRefToEdit] = useState<BenefitRef | undefined>();
  const [adjustedResult, setAdjustedResult] = useState<BulkActivationResultItemDTO | undefined>();

  const handleDeleteBenefit = useCallback(() => {
    deleteActivation(activationToEdit!);
    deleteDialog.dialogState.handleClose();
  }, [activationToEdit, deleteActivation, deleteDialog.dialogState]);

  const hrSystemWarning = useMemo(() => {
    if (
      activationToEdit?.benefitTemplateId &&
      employee.hrSystemBenefitTemplateId &&
      activationToEdit.benefitTemplateId === employee.hrSystemBenefitTemplateId &&
      DateTime.fromISO(activationToEdit.startDate).diffNow('months').months <= 0
    ) {
      return (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('hrSystemTemplateOverrideWarning')}
        </Alert>
      );
    }
    return null;
  }, [activationToEdit, employee.hrSystemBenefitTemplateId, t]);

  const ActivationDialogComponent = useBoundActivationComponent(ActivationDialog, activationToEdit?.benefit || benefitRefToEdit?.benefit);
  const activationSaveFunctions = useEmployeeActivationSaveFunctions(
    activationToEdit?.benefit || benefitRefToEdit?.benefit!,
    employee.id,
    activationToEdit?.customBenefitOptions?.customBenefitId || benefitRefToEdit?.customBenefitId,
    setAdjustedResult,
  );

  const handleOpenActivationCreate = useCallback(
    (benefit: BenefitActivationDTOBenefitEnum, customBenefitId?: string) => {
      setActivationToEdit(undefined);
      setBenefitRefToEdit({ benefit, customBenefitId });
      activationDialog.handleOpenDialogToCreate();
    },
    [activationDialog],
  );

  const handleOpenActivationEdit = useCallback(
    (activation: BenefitTemplateItemDTO | BenefitActivationDTO) => {
      const benefitActivation = activation as BenefitActivationDTO;

      setActivationToEdit(benefitActivation);
      setBenefitRefToEdit({ benefit: activation.benefit, customBenefitId: benefitActivation.customBenefitOptions?.customBenefitId });

      activationDialog.handleOpenDialogToEdit(benefitActivation);
    },
    [activationDialog],
  );

  const handleOpenActivationStop = useCallback(
    (activation: BenefitTemplateItemDTO | BenefitActivationDTO) => {
      const benefitActivation = activation as BenefitActivationDTO;
      setActivationToEdit(benefitActivation);
      setBenefitRefToEdit({ benefit: activation.benefit, customBenefitId: benefitActivation.customBenefitOptions?.customBenefitId });

      stopDialog.handleOpen();
    },
    [stopDialog],
  );
  const handleOpenActivationDelete = useCallback(
    (activation: BenefitTemplateItemDTO | BenefitActivationDTO) => {
      const benefitActivation = activation as BenefitActivationDTO;
      setActivationToEdit(benefitActivation);
      setBenefitRefToEdit({ benefit: activation.benefit, customBenefitId: benefitActivation.customBenefitOptions?.customBenefitId });

      deleteDialog.handleOpen();
    },
    [deleteDialog],
  );

  return (
    <ProfileBenefitActionsContext.Provider
      value={{
        onBenefitCreate: handleOpenActivationCreate,
        onBenefitEdit: handleOpenActivationEdit,
        onBenefitDelete: handleOpenActivationDelete,
        onBenefitStop: handleOpenActivationStop,
      }}
    >
      <BenefitProvider
        benefit={activationToEdit?.benefit || benefitRefToEdit?.benefit!}
        customBenefitId={benefitRefToEdit?.customBenefitId}
        showLoadingIndicator={false}
      >
        <ActivationDialogComponent
          saveFunctions={activationSaveFunctions}
          activationDialogState={activationDialog}
          warnings={hrSystemWarning}
          terminationDate={employee.terminationDate ? DateTime.fromISO(employee.terminationDate) : undefined}
          employee={employee}
        />
        {activationToEdit && (
          <SingleEditActivationDialog
            dialogState={stopDialog.dialogState}
            employeeId={employee.id}
            activationToStop={activationToEdit}
            terminationDate={employee.terminationDate ? DateTime.fromISO(employee.terminationDate) : undefined}
          />
        )}
      </BenefitProvider>
      {activationToEdit && <DeleteActivationDialog dialogState={deleteDialog.dialogState} onDelete={handleDeleteBenefit} />}
      {adjustedResult && (
        <SingleActivationResultDialog
          dialogState={{ isOpen: true, handleClose: () => setAdjustedResult(undefined) }}
          variant="deactivate"
          result={adjustedResult}
        />
      )}
      {children}
    </ProfileBenefitActionsContext.Provider>
  );
};
